import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";
import { getApiErrorMessage } from "../../utils/apiError";

import { receivableTemplateApi } from "../../api/receivableTemplateApi";

import type {
  ReceivableTemplate,
  ReceivableTemplateSearchPayload,
} from "../../types/receivableTemplateTypes";

import {
  uploadMediaFiles,
  createResolvedMedia,
  buildRenamePayload,
  applyRename,
  buildDeletePayload,
  applyDelete,
} from "../../utils/mediaUtils";
import { mediaLinkKey, resolveMediaLinks } from "../../utils/mediaLinkBatch";

export const NOTICE_TEMPLATE_TYPE = 2;
export const ALL_CATEGORY = 1;
/** "Use for" values a template can carry: All, Reminder emails, Formal notices, Legal notices. */
export const TEMPLATE_CATEGORIES = [1, 2, 3, 4];

interface NoticeTemplatesState {
  items: ReceivableTemplate[];
  selectedTemplate: ReceivableTemplate | null;

  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;

  hasMore: boolean;
  pageIndex: number;
  totalCount: number;

  error: string | null;
}

const initialState: NoticeTemplatesState = {
  items: [],
  selectedTemplate: null,

  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,

  hasMore: true,
  pageIndex: 0,
  totalCount: 0,

  error: null,
};

const noticeTemplatesSlice = createSlice({
  name: "noticeTemplates",

  initialState,

  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },

    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },

    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },

    setNoticeTemplates: (
      state,
      action: PayloadAction<ReceivableTemplate[]>,
    ) => {
      state.items = action.payload;
    },

    setSelectedTemplate: (
      state,
      action: PayloadAction<ReceivableTemplate | null>,
    ) => {
      state.selectedTemplate = action.payload;
    },

    appendNoticeTemplates: (
      state,
      action: PayloadAction<ReceivableTemplate[]>,
    ) => {
      state.items = [...state.items, ...action.payload];
    },

    addSingleNoticeTemplateLocally: (
      state,
      action: PayloadAction<ReceivableTemplate>,
    ) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },

    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },

    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },

    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },

    resetNoticeTemplateList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
      state.selectedTemplate = null;
      state.error = null;
    },

    updateSingleNoticeTemplate: (
      state,
      action: PayloadAction<
        Partial<ReceivableTemplate> & {
          id: string;
        }
      >,
    ) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );

      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          ...action.payload,
        };
      }

      if (state.selectedTemplate?.id === action.payload.id) {
        state.selectedTemplate = {
          ...state.selectedTemplate,
          ...action.payload,
        };
      }
    },

    removeSingleNoticeTemplate: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);

      if (state.selectedTemplate?.id === action.payload) {
        state.selectedTemplate = null;
      }

      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,

  setNoticeTemplates,
  setSelectedTemplate,
  appendNoticeTemplates,

  addSingleNoticeTemplateLocally,

  setTotalCount,
  setHasMore,
  setPageIndex,

  resetNoticeTemplateList,

  updateSingleNoticeTemplate,
  removeSingleNoticeTemplate,
} = noticeTemplatesSlice.actions;

// The newest first-page request. A fresh search term must not be dropped
// because an earlier one is still loading, so a first page always runs and an
// answer to anything older than it is discarded.
let latestFirstPage = 0;

export const fetchNoticeTemplates = (
  payload: ReceivableTemplateSearchPayload = {},
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().noticeTemplates;

    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) {
      return;
    }
    const ticket = isFirstPage ? ++latestFirstPage : latestFirstPage;
    const superseded = () => ticket !== latestFirstPage;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        companyId: userData.companyId,

        isAppAdmin: userData.isAppAdmin ?? true,

        pageSize: payload.pageSize || 10,

        pageIndex: incomingPageIndex,

        orderBy: payload.orderBy || "modificationdate",

        sortAscending: payload.sortAscending ?? false,

        includeFacets: true,

        groupIds: [`${userData.companyId}`],

        includeDocument: payload.includeDocument !== false,

        ids: payload.ids || [],

        // Admins' "Show Deleted" filter.

        includeDeleted: payload.includeDeleted ?? false,

        categories: payload.categories?.length ? payload.categories : TEMPLATE_CATEGORIES,

        templateType: NOTICE_TEMPLATE_TYPE,

        isActive: payload.isActive ?? null,

        createdBy: (payload.createdBy || []).map((item: any) =>
          typeof item === "object" ? item.id : item,
        ),

        updatedBy: (payload.updatedBy || []).map((item: any) =>
          typeof item === "object" ? item.id : item,
        ),

        currentProfileId: userData.currentLoggedInUserId,

        myMode: payload.myMode ?? true,

        searchText:
          typeof payload.searchText === "object"
            ? payload.searchText?.label || payload.searchText?.id || ""
            : String(payload.searchText || "").trim(),

        ...(payload.startDate
          ? {
              startDate: new Date(payload.startDate).toISOString(),
            }
          : {}),

        ...(payload.endDate
          ? {
              endDate: new Date(payload.endDate).toISOString(),
            }
          : {}),
      };

      const response =
        await receivableTemplateApi.searchReceivableTemplate(requestBody);

      if (superseded()) return;

      const fetchedData: ReceivableTemplate[] = response.data || [];

      const totalCount = response.count || 0;

      dispatch(setTotalCount(totalCount));

      // One media request for the page, not one per template.
      const ATTACHMENT_MEDIA_TYPE = 3;
      const links = await resolveMediaLinks(
        fetchedData,
        (item, want) => {
          for (const media of (item.medias || []) as any[]) {
            want(item.groupId, ATTACHMENT_MEDIA_TYPE, media.s3Url);
          }
        },
        receivableTemplateApi.getMediaLink,
        "Notice Template",
      );

      if (superseded()) return;

      const resolvedData = fetchedData.map((templateItem: ReceivableTemplate) => {
        const updatedTemplate = {
          ...templateItem,
          id: templateItem.id,
        };

        if (templateItem.medias && templateItem.medias.length > 0) {
          updatedTemplate.medias = templateItem.medias.map((media: any) => ({
            ...media,
            url: links.get(mediaLinkKey(ATTACHMENT_MEDIA_TYPE, media.s3Url)) || "",
          }));
          updatedTemplate.mediaLinks = null;
        }

        return updatedTemplate;
      });

      const currentLoadedCount =
        incomingPageIndex === 0
          ? resolvedData.length
          : state.items.length + resolvedData.length;

      if (currentLoadedCount >= totalCount || resolvedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setNoticeTemplates(resolvedData));

        dispatch(setPageIndex(1));
      } else {
        dispatch(appendNoticeTemplates(resolvedData));

        dispatch(setPageIndex(incomingPageIndex + 1));
      }

      return resolvedData;
    } catch (error: any) {
      console.error("Fetch Notice Templates Error:", error);

      dispatch(setHasMore(false));

      toast.error(
        i18n.t("common:slices.noticeTemplates.fetchError", {
          defaultValue: "Failed to fetch notice templates",
        }),
      );

      throw error;
    } finally {
      if (!superseded()) dispatch(setLoading(false));
    }
  };
};

export const getNoticeTemplateById = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));

      const result = await dispatch(
        fetchNoticeTemplates({
          ids: [id],
          pageIndex: 0,
          pageSize: 1,
        }),
      );

      const template = Array.isArray(result) ? result[0] : undefined;

      if (!template) {
        throw new Error(i18n.t("slices.noticeTemplates.notFound", { ns: "common" }));
      }

      dispatch(setSelectedTemplate(template));

      return template;
    } catch (error) {
      console.error("Get Notice Template Error:", error);

      toast.error(
        i18n.t("common:slices.noticeTemplates.fetchDetailError", {
          defaultValue: "Failed to fetch notice template details",
        }),
      );

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addNoticeTemplate = (data: ReceivableTemplate) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setAdding(true));

      const apiPayload = {
        ...data,

        templateType: NOTICE_TEMPLATE_TYPE,

        category: data.category ?? ALL_CATEGORY,
      };

      const res =
        await receivableTemplateApi.createReceivableTemplate(apiPayload);

      const newTemplate: ReceivableTemplate = {
        ...data,

        id: res?.id || res,

        templateType: NOTICE_TEMPLATE_TYPE,

        category: data.category ?? ALL_CATEGORY,

        isActive: true,

        medias: data.medias || [],

        receivableTemplatePlaceholders:
          data.receivableTemplatePlaceholders || [],

        mediaLinks: data.mediaLinks || null,

        placeholders: data.placeholders || null,

        creationDate: new Date().toISOString(),

        modificationDate: new Date().toISOString(),

        createdBy: data.createdBy || "",

        modifiedBy: data.modifiedBy || "",

        createdByName: data.createdByName || "",

        modifiedByName: data.modifiedByName || "",

        receivableTemplateName: data.receivableTemplateName || "",

        htmlContent: data.htmlContent || "",

        receivableTemplateJson: data.receivableTemplateJson || "",
      };

      const { items } = getState().noticeTemplates;

      dispatch(setNoticeTemplates([newTemplate, ...items]));

      toast.success(
        i18n.t("noticeTemplate:noticeTemplate.messages.addSuccess", {
          defaultValue: "Notice Template created successfully",
        }),
      );

      return res;
    } catch (error) {
      console.error("Create Notice Template Error:", error);

      toast.error(
        i18n.t("noticeTemplate:noticeTemplate.messages.addError", {
          defaultValue: "Failed to create notice template",
        }),
      );

      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editNoticeTemplate = (
  data: ReceivableTemplate & {
    id: string;
  },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setEditing(true));

      const apiPayload = {
        ...data,

        id: data.id,

        templateType: NOTICE_TEMPLATE_TYPE,

        category: data.category ?? ALL_CATEGORY,
      };

      await receivableTemplateApi.updateReceivableTemplate(apiPayload);

      const { items } = getState().noticeTemplates;

      const updatedList = items.map((item: ReceivableTemplate) =>
        item.id === data.id
          ? {
              ...item,
              ...data,
              templateType: NOTICE_TEMPLATE_TYPE,
              category: data.category ?? ALL_CATEGORY,
            }
          : item,
      );

      dispatch(setNoticeTemplates(updatedList));

      toast.success(
        i18n.t("noticeTemplate:noticeTemplate.messages.editSuccess", {
          defaultValue: "Notice Template updated successfully",
        }),
      );

      return data;
    } catch (error) {
      console.error("Edit Notice Template Error:", error);

      toast.error(
        i18n.t("noticeTemplate:noticeTemplate.messages.editError", {
          defaultValue: "Failed to update notice template",
        }),
      );

      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const uploadNoticeTemplateImages = (
  templateId: string,
  files: File[],
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!files?.length) return;

      const userId = (await getCurrentUser()).userId;

      const { payloads, links } = await uploadMediaFiles(
        receivableTemplateApi,
        templateId,
        files,
      );

      const newEntries = createResolvedMedia(
        payloads,
        links,
        templateId,
        userId,
      );

      const state = getState();

      const updatedItems = state.noticeTemplates.items.map(
        (item: ReceivableTemplate) =>
          item.id === templateId
            ? {
                ...item,

                medias: [...(item.medias || []), ...newEntries],

                mediaLinks: null,
              }
            : item,
      );

      dispatch(setNoticeTemplates(updatedItems));

      toast.success(
        i18n.t("common:slices.noticeTemplates.uploadSuccess", {
          count: files.length,
          defaultValue: `${files.length} files uploaded successfully`,
        }),
      );
    } catch (error) {
      console.error("Notice Template Upload Error:", error);

      toast.error(
        i18n.t("common:slices.noticeTemplates.uploadError", {
          defaultValue: "Failed to upload files",
        }),
      );

      throw error;
    }
  };
};

export const renameNoticeTemplateImage = (
  templateId: string,
  fileId: string,
  newName: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();

      const currentTemplate = state.noticeTemplates.items.find(
        (item: ReceivableTemplate) => item.id === templateId,
      );

      const targetFile = currentTemplate?.medias?.find(
        (media: any) => media.id === fileId,
      );

      if (!targetFile) {
        toast.error(i18n.t("slices.shared.fileNotFound", { ns: "common" }));
        return;
      }

      const payload = buildRenamePayload(targetFile, templateId, newName);

      await receivableTemplateApi.uploadMedia(templateId, payload);

      const updatedItems = state.noticeTemplates.items.map(
        (item: ReceivableTemplate) =>
          item.id === templateId
            ? {
                ...item,
                medias: applyRename(item.medias || [], fileId, newName),
              }
            : item,
      );

      dispatch(setNoticeTemplates(updatedItems));
    } catch (error) {
      console.error("Notice Template Media Rename Error:", error);

      toast.error(
        i18n.t("common:slices.noticeTemplates.renameError", {
          defaultValue: "Failed to rename file",
        }),
      );

      throw error;
    }
  };
};

export const removeNoticeTemplateImage = (
  templateId: string,
  filesToRemove: Array<{
    id: string;
  }>,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!filesToRemove?.length) {
        return;
      }

      const state = getState();

      const currentTemplate = state.noticeTemplates.items.find(
        (item: ReceivableTemplate) => item.id === templateId,
      );

      if (!currentTemplate?.medias) {
        return;
      }

      const idsToRemove = filesToRemove.map((file) => file.id);

      const payload = buildDeletePayload(
        currentTemplate.medias,
        templateId,
        idsToRemove,
      );
      // Nothing to remove that this record still lists.
      if (payload.length === 0) return;

      await receivableTemplateApi.uploadMedia(templateId, payload);

      const updatedItems = state.noticeTemplates.items.map(
        (item: ReceivableTemplate) =>
          item.id === templateId
            ? {
                ...item,
                medias: applyDelete(item.medias || [], idsToRemove),
              }
            : item,
      );

      dispatch(setNoticeTemplates(updatedItems));
    } catch (error) {
      console.error("Notice Template Remove Media Error:", error);

      toast.error(
        i18n.t("common:slices.noticeTemplates.deleteImageError", {
          defaultValue: "Failed to remove file",
        }),
      );

      throw error;
    }
  };
};

export const handleActivateNoticeTemplate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setLoading(true));

      await receivableTemplateApi.activateTemplate(id);

      const { items } = getState().noticeTemplates;

      dispatch(
        setNoticeTemplates(
          items.map((item: ReceivableTemplate) =>
            item.id === id
              ? {
                  ...item,
                  isActive: true,
                }
              : item,
          ),
        ),
      );

      toast.success(
        i18n.t("common:slices.noticeTemplates.activateSuccess", {
          defaultValue: "Notice Template activated successfully",
        }),
      );
    } catch (error) {
      console.error("Activate Notice Template Error:", error);

      toast.error(
        i18n.t("common:slices.noticeTemplates.activateError", {
          defaultValue: "Failed to activate notice template",
        }),
      );

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const handleDeactivateNoticeTemplate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setLoading(true));

      await receivableTemplateApi.deActivateTemplate(id);

      const { items } = getState().noticeTemplates;

      dispatch(
        setNoticeTemplates(
          items.map((item: ReceivableTemplate) =>
            item.id === id
              ? {
                  ...item,
                  isActive: false,
                }
              : item,
          ),
        ),
      );

      toast.success(
        i18n.t("common:slices.noticeTemplates.deactivateSuccess", {
          defaultValue: "Notice Template deactivated successfully",
        }),
      );
    } catch (error) {
      console.error("Deactivate Notice Template Error:", error);

      toast.error(
        i18n.t("common:slices.noticeTemplates.deactivateError", {
          defaultValue: "Failed to deactivate notice template",
        }),
      );

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const removeNoticeTemplate = (id: string, reason: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setDeleting(true));

      await receivableTemplateApi.removeReceivableTemplate(id, reason);

      const { items } = getState().noticeTemplates;

      dispatch(
        setNoticeTemplates(
          items.filter((item: ReceivableTemplate) => item.id !== id),
        ),
      );

      toast.success(
        i18n.t("noticeTemplate:noticeTemplate.messages.removeSuccess", {
          defaultValue: "Notice Template removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Notice Template Error:", error);

      toast.error(
        getApiErrorMessage(
          error,
          i18n.t("noticeTemplate:noticeTemplate.messages.removeError", {
            defaultValue: "Failed to remove notice template",
          }),
        ),
      );

      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default noticeTemplatesSlice.reducer;
